import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spin,
  Alert,
  Button,
  message,
  Tag,
  Select,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  CreditCardOutlined,
  DollarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  fetchAmortizationsByPatient,
  Amortization,
} from "../../../api/amortization.api";
import { getPacienteById, Paciente } from "../../../api/paciente.api";
import { fetchTreatmentPlanByPatient } from "../../../api/treatmentPlan.api";
import { N1coPayService } from "../../../utils/N1co/services/N1coPayService";
import { PaymentProviderService } from "../../../utils/wompi/api/paymentProvider";
import type { PaymentLinkResult } from "../../../utils/N1co/types/n1co.types";
import type { PaymentProvider } from "../../../utils/wompi/types/types";

// ✅ 1. AÑADIMOS 'Paragraph' AQUÍ
const { Title, Text, Paragraph } = Typography;

interface EstadoDeCuentaProps {
  pacienteId: number;
}

const EstadoDeCuenta: React.FC<EstadoDeCuentaProps> = ({ pacienteId }) => {
  const [amortizations, setAmortizations] = useState<Amortization[]>([]);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<{ [key: number]: boolean }>({});
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>("n1co");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Traducción y color de estado
  const translateStatus = (status: string): string => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "Pagado";
      case "pending":
        return "Pendiente";
      default:
        return status || "";
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Generar link de pago
  const handleGeneratePaymentLink = async (amortization: Amortization) => {
    if (!paciente?.email) {
      message.error("El paciente debe tener un email para generar el link");
      return;
    }

    setPaymentLoading((prev) => ({
      ...prev,
      [amortization.amortizationId]: true,
    }));

    const customerName = [
      paciente.nombre1,
      paciente.nombre2,
      paciente.apellido1,
      paciente.apellido2,
    ]
      .filter(Boolean)
      .join(" ");

    try {
      let result: PaymentLinkResult;
      let webhookUrl: string;

      if (selectedProvider === "n1co") {
        webhookUrl = PaymentProviderService.generateWebhookUrl("n1co");
        result = await N1coPayService.createPaymentLinkForAmortization({
          amortizationId: amortization.amortizationId,
          amount: amortization.amount,
          description: N1coPayService.generatePaymentDescription(amortization),
          customerName,
          customerEmail: paciente.email,
          customerPhone: paciente.celular || paciente.telefono,
        });
      } else {
        webhookUrl = PaymentProviderService.generateWebhookUrl(selectedProvider);
        const providerResult = await PaymentProviderService.createPaymentLinkWithProvider(
          selectedProvider,
          amortization.amortizationId,
          {
            emailsNotificacion: paciente.email,
            urlRedirect: PaymentProviderService.generateSuccessRedirectUrl(
              amortization.amortizationId,
              selectedProvider
            ),
            urlWebhook: webhookUrl,
          }
        );

        result = {
          success: providerResult.success,
          paymentUrl: providerResult.paymentUrl,
          error: providerResult.error,
        };
      }

      if (result.success && result.paymentUrl) {
        let urlToOpen = result.paymentUrl;
        if (!urlToOpen.startsWith("http")) {
          urlToOpen = "https://" + urlToOpen;
        }
        message.success("Redirigiendo al pago...");
        setTimeout(() => {
          window.location.href = urlToOpen;
        }, 1000);
      } else {
        message.error(result.error || "Error generando link de pago");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error inesperado generando el link";
      message.error(errorMessage);
    } finally {
      setPaymentLoading((prev) => ({
        ...prev,
        [amortization.amortizationId]: false,
      }));
    }
  };

  // Cargar datos
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const pacData = await getPacienteById(pacienteId);
        setPaciente(pacData);

        const codigoPaciente = Number(pacData.codigoPaciente);
        if (isNaN(codigoPaciente)) {
          throw new Error("Código de paciente inválido");
        }

        const [amortizationsData, plansData] = await Promise.all([
          fetchAmortizationsByPatient(codigoPaciente),
          fetchTreatmentPlanByPatient(codigoPaciente),
        ]);

        const enriched = amortizationsData.map((amortization) => {
          const plan = plansData.find(
            (p) => p.treatmentPlanId === amortization.treatmentPlanId
          );
          return {
            ...amortization,
            treatmentName:
              amortization.concept ||
              amortization.treatmentName ||
              plan?.treatmentName ||
              "Tratamiento no especificado",
          };
        });

        // ✅ NUEVO PASO 1: Calcular fechas máximas por plan
        const planMaxDateMap = new Map<number, Date>();
        for (const amort of enriched) {
          const currentDate = new Date(amort.paymentDate);
          const maxDate = planMaxDateMap.get(amort.treatmentPlanId);
          if (!maxDate || currentDate > maxDate) {
            planMaxDateMap.set(amort.treatmentPlanId, currentDate);
          }
        }

        // ✅ NUEVO PASO 2: Ordenar la lista enriquecida
        enriched.sort((a, b) => {
          // Obtenemos la fecha MÁXIMA de sus respectivos grupos
          const maxDateA = planMaxDateMap.get(a.treatmentPlanId)!.getTime();
          const maxDateB = planMaxDateMap.get(b.treatmentPlanId)!.getTime();

          // 1. Ordenar GRUPOS por fecha máxima (descendente, más reciente primero)
          if (maxDateA !== maxDateB) {
            return maxDateB - maxDateA;
          }

          // 2. Si son del mismo grupo, ordenar CUOTAS por fecha (ascendente, más antigua primero)
          const dateA = new Date(a.paymentDate).getTime();
          const dateB = new Date(b.paymentDate).getTime();
          return dateA - dateB;
        });

        setAmortizations(enriched);
      } catch (error: unknown) {
        console.error("Error cargando datos:", error);
        setError((error as Error).message || "Error cargando datos");
      } finally {
        setLoading(false);
      }
    };

    loadData();

    const handleReload = () => setTimeout(() => loadData(), 800);
    document.addEventListener("visibilitychange", handleReload);
    window.addEventListener("focus", handleReload);
    window.addEventListener("pageshow", handleReload);

    return () => {
      document.removeEventListener("visibilitychange", handleReload);
      window.removeEventListener("focus", handleReload);
      window.removeEventListener("pageshow", handleReload);
    };
  }, [pacienteId]);

  // Refrescar datos manualmente
  const handleRefreshData = async () => {
    setLoading(true);
    try {
      if (!paciente) return;
      const codigoPaciente = Number(paciente.codigoPaciente);
      const [amortizationsData, plansData] = await Promise.all([
        fetchAmortizationsByPatient(codigoPaciente),
        fetchTreatmentPlanByPatient(codigoPaciente),
      ]);

      const enriched = amortizationsData.map((amortization) => {
        const plan = plansData.find(
          (p) => p.treatmentPlanId === amortization.treatmentPlanId
        );
        return {
          ...amortization,
          treatmentName:
            amortization.concept ||
            amortization.treatmentName ||
            plan?.treatmentName ||
            "Tratamiento no especificado",
        };
      });

      // ✅ NUEVO PASO 1: Calcular fechas máximas por plan
      const planMaxDateMap = new Map<number, Date>();
      for (const amort of enriched) {
        const currentDate = new Date(amort.paymentDate);
        const maxDate = planMaxDateMap.get(amort.treatmentPlanId);
        if (!maxDate || currentDate > maxDate) {
          planMaxDateMap.set(amort.treatmentPlanId, currentDate);
        }
      }

      // ✅ NUEVO PASO 2: Ordenar la lista enriquecida
      enriched.sort((a, b) => {
        // Obtenemos la fecha MÁXIMA de sus respectivos grupos
        const maxDateA = planMaxDateMap.get(a.treatmentPlanId)!.getTime();
        const maxDateB = planMaxDateMap.get(b.treatmentPlanId)!.getTime();

        // 1. Ordenar GRUPOS por fecha máxima (descendente, más reciente primero)
        if (maxDateA !== maxDateB) {
          return maxDateB - maxDateA;
        }

        // 2. Si son del mismo grupo, ordenar CUOTAS por fecha (ascendente, más antigua primero)
        const dateA = new Date(a.paymentDate).getTime();
        const dateB = new Date(b.paymentDate).getTime();
        return dateA - dateB;
      });

      setAmortizations(enriched);
      message.success("Datos actualizados exitosamente");
    } catch {
      message.error("Error al actualizar los datos");
    } finally {
      setLoading(false);
    }
  };

  if (error) return <Alert type="error" message={error} showIcon />;

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) setPageSize(newPageSize);
  };

  // Columnas de la tabla
  const columns: ColumnsType<Amortization> = [
    // La columna '#' se recalculará en cada renderizado de la tabla paginada
    {
      title: "#",
      key: "index",
      render: (_, __, index) => {
        // Calculamos el índice basado en la página actual
        const i = (currentPage - 1) * pageSize + index + 1;
        return <Text strong>{i}</Text>;
      },
      width: 20,
    },
    {
      title: "Tratamiento / Concepto", // Título actualizado
      dataIndex: "treatmentName",
      key: "treatmentName",
      width: 150, // Tu nuevo ancho
      // ✅ 2. AQUI HACEMOS EL CAMBIO
      render: (text, record, index) => {
        const prevRecord = amortizations[(currentPage - 1) * pageSize + index - 1];
        const isFirstInGroup =
          !prevRecord ||
          prevRecord.treatmentPlanId !== record.treatmentPlanId;
        
        return {
          children: (
            // Usamos Paragraph en lugar de Text
            <Paragraph
              strong={isFirstInGroup}
              ellipsis={{ rows: 3, tooltip: text }}
              style={{ margin: 0 }} // Importante para quitar márgenes
            >
              {text}
            </Paragraph>
          ),
        };
      },
    },
    {
      title: "Fecha de Pago",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (d) => <Text>{new Date(d).toLocaleDateString("es-ES")}</Text>,
      width: 100,
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      render: (a) => (
        <Text strong style={{ color: "#52c41a" }}>
          ${a.toFixed(2)}
        </Text>
      ),
      width: 75,
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={getStatusColor(s)}>{translateStatus(s)}</Tag>
      ),
      width: 75,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 200,
      render: (_, rec) =>
        rec.status === "pending" ? (
          <Space>
            <Select
              value={selectedProvider}
              onChange={setSelectedProvider}
              style={{ width: 90 }}
              size="small"
            >
              <Select.Option value="n1co">N1co</Select.Option>
              <Select.Option value="wompi">Wompi</Select.Option>
            </Select>
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              loading={paymentLoading[rec.amortizationId]}
              onClick={() => handleGeneratePaymentLink(rec)}
              size="small"
            >
              Pagar
            </Button>
          </Space>
        ) : (
          <Tag color="success" icon={<DollarOutlined />}>
            Pagado
          </Tag>
        ),
    },
  ];

  return (
    <div className="account-status-container">
      <Card bordered={false} style={{ borderRadius: "8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <Title level={3}>Estado de Cuenta</Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefreshData}
            loading={loading}
          >
            Actualizar
          </Button>
        </div>

        {loading ? (
          <Spin size="large" tip="Cargando estado de cuenta..." />
        ) : (
          <>
            <Alert
              message={
                <>
                  <Text strong>Instrucciones:</Text> Utiliza el botón “Pagar” para generar un enlace de pago y verifica el estado actualizado.
                  Si no visualizas correctamente los botones, desliza hacia la derecha para acceder a toda la información.
                </>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Divider />

            <Table
              dataSource={amortizations}
              columns={columns}
              rowKey="amortizationId"
              pagination={{
                current: currentPage,
                pageSize,
                total: amortizations.length,
                onChange: handlePageChange,
                showSizeChanger: true,
              }}
              scroll={{ x: 900 }}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default EstadoDeCuenta;